/*
 * PREFERENCE STORE SAGAS
 *
 * These routines listen for request actions, and satisfies them.  These are asynchronous routines, and upon
 * completion will dispatch a new action with the results
 *
 *
 */
import { call, put, takeEvery, select } from 'redux-saga/effects';
import * as accountsActions from 'data/accounts/actions';

import * as accountSelectors from 'data/accounts/selectors';
import * as preferencesSelectors from 'data/preferences/selectors';
import * as datasetSelectors from 'utils/auth/selectors';

import { defaultDatasetPreferences } from 'data/preferences/defaults';
import { getLogger } from 'utils/logger';
import AxiosFactory from 'utils/axiosFactory';
import { Config, ENVIRONMENT } from 'utils/webappConfig';
import { requiredColumns, fieldData } from 'components/TransactionRegister/transactionsConfig';

import { initAccountPrefs } from './utils';

import * as actions from './actions';

const log = getLogger('data/preferences/sagas.js');

// ===============================================================================================
// SAGAS functions for preferences.  These are the functions responsible for communicating directly
// with external endpoints asynchronously.  Data is returned via ACTIONS, however, these can be
// split out (and should be) to be fully functional and JS shareable API callers via Axios and
// simply return an object in a provided callback, which can then choose to call the actions
//

function constructPreferencesUrl(data) {

  let requestUrl;
  let urlSection;

  switch (data.section) {
    case 'shared':
      urlSection = data.group === 'dataset' ? '' : `/${data.group}`;
      requestUrl = `${Config.environments[ENVIRONMENT].services_url}/preferences${urlSection}`;
      break;

    case 'client':
      urlSection = data.group === 'dataset' ? '' : `/${data.group}`;
      requestUrl = `${Config.environments[ENVIRONMENT].services_url}/preferences/client${urlSection}`;
      break;

    default:
      requestUrl = `${Config.environments[ENVIRONMENT].services_url}/preferences`;
      break;

  }

  return requestUrl;
}

// ====================================================
// GET PREFERENCES
//
// specify the section and they group as follows { section: <string>, group: <string> }
//
// SECTION: one of 'shared', 'client', 'qcs'
// GROUP: shared, one of 'user', 'dataset;
//        client, one of 'user', 'dataset', 'global'
//        qcs: only top level
//
export function* getPreferences({ payload: data }) {
  log.log('Getting preferences...', data);

  try {
    const qcsAxios = AxiosFactory.get('qcs');

    const requestUrl = constructPreferencesUrl(data);

    // ASYNC CALL HERE
    const response = yield call(qcsAxios.get, requestUrl, data.group === 'user' && { headers: { 'qcs-dataset-id': null } });

    // SUCCESS CALLBACK
    //
    // convert the preference array to an immutable object
    // you can do any transformation here to convert from 3rd party format
    // to your internal state format

    // remove deleted

    log.log('Got Preferences', response.data, data);

    // this does nothing actually except set loading
    yield put(actions.getPreferencesSuccess({ resources: response.data, path: data }));

  } catch (error) {
    // ERROR CALLBACK
    // todo: make sure this is an axios error
    log.log(error);
    yield put(actions.getPreferencesFailure(error));
  }
}


// ====================================================
// SET PREFERENCE
// if you want to use yield, put a star here
//
// Pass in an object { section, group, preference }
//
// SECTIONS: one of 'shared', 'client', 'qcs'
// GROUP: shared, one of 'user', 'dataset;
//        client, one of 'user', 'dataset', 'global'
//        qcs: only top level
//
//
// preference is a object structure following the rules described here (do not send JSON, we convert for you)
// https://quickenteam.atlassian.net/wiki/spaces/EN/pages/99037174/Preference+Service
//
//
export function* setPreference({ payload: data }) {

  log.log('Setting preferences...', data);

  const qcsAxios = AxiosFactory.get('qcs');

  const { preference } = data;

  try {

    const requestUrl = constructPreferencesUrl(data);

    const requestBody = {
      resources: preference,
    };
    log.log(`In SAGAS: SET/CREATE Preference ${requestUrl}`, JSON.stringify(preference));

    // optimistic update, currently no error handling for preferences (no undo)

    yield put(actions.setPreferenceSuccess({ data, response: { data: preference } } ));

    log.log(`In SAGAS: SET/CREATE Preference ${requestUrl}`, JSON.stringify(preference));

    // Real Update
    yield call(qcsAxios.put, requestUrl, requestBody.resources, data.group === 'user' && { headers: { 'qcs-dataset-id': null } });
    // entire new preferences structure is returned in the response, we place it into Redux

    // yield put(actions.setPreferenceSuccess({ data, response }));

  } catch (error) {
    // log.debug('Error updating category', error);
    // Undo the optimistic update

    // Indicate the error
    yield put(actions.setPreferenceFailure(error));
  }

}

export function* resetAllPreferences() {

  try {
    const qcsAxios = AxiosFactory.get('qcs');
    const data = {
      section: 'shared',
      group: 'dataset',
      preference: null,
    };
    // first set redux prefs to defaults with a faux response
    yield put(actions.setPreferenceSuccess({
      data,
      response: { data: defaultDatasetPreferences },
    }));

    // now null out the prefs on QCS
    const requestUrl = constructPreferencesUrl(data);

    yield call(qcsAxios.put, requestUrl, { webApp: null }, data.group === 'user' && { headers: { 'qcs-dataset-id': null } });
  } catch (error) {
    log.log('Error in resetting preferences');
  }
}

// ====================================================
// DELETE PREFERENCE
// if you want to use yield, put a star here
export function* deletePreference({ payload: _data }) {
  /*
    let requestUrl = `${Config.environments[ENVIRONMENT].services_url}/preferences`;
    requestUrl += `/${data.id}`;
    log.log(`In SAGAS: Delete Preference ${requestUrl}`, data);

    try {
      const qcsAxios = AxiosFactory.get('qcs');

      // yield below causes the call to happen async, code following it is the
      // callback functions (one for success, one for error)
      //
      // assumes that the data passed in is valid and per the QCS spec
      yield call(qcsAxios.delete, requestUrl, data);

      // create an immutable object
      // const preference = types.Preference(data);
      yield put(actions.deletePreferenceSuccess(data.id));
    } catch (error) {
      log.log('Update Preference Error...', error);
      yield put(actions.deletePreferenceFailure(error));
    }
  */
}

// ====================================================
// INIT ACCOUNT PREFERENCES
export function* initAccountPreferences() {

  const accountsPending = yield select(accountSelectors.getLoadPending);
  const prefsPending = yield select(preferencesSelectors.getLoadPending);

  if (!accountsPending && !prefsPending.shared.dataset) {
    const accounts = yield select(accountSelectors.getAccountsById);
    const acctPrefs = yield select(preferencesSelectors.allAccountPreferences);

    if (accounts) {
      const newAcctPrefs = initAccountPrefs(accounts, acctPrefs);

      if (newAcctPrefs) {

        const prefObj = {
          section: 'shared',
          group: 'dataset',
          preference: { webApp: { accountsPrefs: newAcctPrefs } },
        };
        yield put(actions.setPreference(prefObj));
      }
    }
    //
    // Also check if the transaction columns config was updated with new defaults
    // if the required defaults in the code contain any columns NOT in the registerPrefs, then
    // add required defaults in
    //
    const prefs = yield select(preferencesSelectors.getSharedPreferencesByPath,
      { group: 'dataset', path: ['webApp'] });

    const regFields = prefs.transactionRegister.regFieldsSort.cashFlow;

    let newFields = regFields;
    requiredColumns.forEach((col) => {
      if (!regFields.includes(col)) {
        const slot = newFields.findIndex((x) => fieldData[x].position > fieldData[col].position);
        newFields = newFields.insert(slot, col);
        // log.debug('COULD NOT FIND ', col, newFields.toJS());
      }
    });
    if (newFields !== regFields) {
      const prefObj = {
        section: 'shared',
        group: 'dataset',
        preference: { webApp: { transactionRegister: { regFieldsSort: { cashFlow: newFields } } } },
      };
      yield put(actions.setPreference(prefObj));
    }

  }
  if (!accountsPending && !prefsPending.shared.user) {

    // ALSO use this to store the current datasetId being used which also is dependent on both
    // accounts and preferences completing loading
    //
    const datasetId = yield select(datasetSelectors.getDatasetId);

    let datasetsUsed = yield select(preferencesSelectors.getSharedPreferencesByPath,
      { group: 'user', path: ['datasetIdsUsed'] });

    if (!datasetsUsed) {
      datasetsUsed = [datasetId];
    } else {
      // delete this dataset if it is already in the array
      const currIndex = datasetsUsed.indexOf(datasetId);
      if (currIndex >= 0) {
        datasetsUsed = datasetsUsed.delete(currIndex);
      }
      // push it to the array
      datasetsUsed = datasetsUsed.insert(0, datasetId);
    }

    // We limit the size of the list to 10 most recently used
    datasetsUsed = datasetsUsed.splice(10);

    const prefObj = {
      section: 'shared',
      group: 'user',
      preference: { datasetIdsUsed: datasetsUsed },
    };

    yield put(actions.setPreference(prefObj));

  }
}

// ====================================================
// ACTION WATCHERS to trigger SAGAS calls

export function* getPreferencesActionWatcher() {
  yield takeEvery(actions.getPreferences, getPreferences);
}

export function* getAccountsSuccessActionWatcher() {
  yield takeEvery(accountsActions.getAccountsSuccess, initAccountPreferences);
}

export function* upsertAccountsSuccessActionWatcher() {
  yield takeEvery(accountsActions.upsertAccountsSuccess, initAccountPreferences);
}

export function* initAccountPreferencesActionWatcher() {
  yield takeEvery(actions.initAccountPreferences, initAccountPreferences);
}

export function* resetAllPreferencesActionWatcher() {
  yield takeEvery(actions.resetAllPreferences, resetAllPreferences);
}

export function* getPreferencesSuccessActionWatcher() {
  yield takeEvery(actions.getPreferencesSuccess, initAccountPreferences);
}

export function* setPreferenceActionWatcher() {
  yield takeEvery(actions.setPreference, setPreference);
}

export function* deletePreferenceActionWatcher() {
  yield takeEvery(actions.deletePreference, deletePreference);
}


// ====================================================
// EXPORTS

export default [
  getPreferencesActionWatcher,
  setPreferenceActionWatcher,
  deletePreferenceActionWatcher,
  getAccountsSuccessActionWatcher,
  getPreferencesSuccessActionWatcher,
  initAccountPreferencesActionWatcher,
  resetAllPreferencesActionWatcher,
  upsertAccountsSuccessActionWatcher,
];
